import { useEffect, useState } from 'react';
import { pollForWindowProperty } from '@cx-web/utils/pollForWindowObject';

import Icon from '../../../../primitives/icon/index';

import { getOcWindow } from './ocWindow';
import styles from './UKBetslip.module.css';

type UKBetslipProps = {
  apiKey: string;
  betslipScriptUrl: string;
  deviceId: string;
  oddsFormat: string;
  repub: string;
};

export const UKBetslip = ({ apiKey, betslipScriptUrl, deviceId, oddsFormat, repub }: UKBetslipProps) => {
  const [betCount, setBetCount] = useState(0);
  const [loadState, setLoadState] = useState<'loading' | 'ready' | 'failed'>('loading');

  useEffect(() => {
    let cancelled = false;
    let intervalId: ReturnType<typeof setInterval> | null = null;
    const ocWindow = getOcWindow();

    ocWindow.oc = ocWindow.oc ?? { betslipAdvertActive: false };
    ocWindow.oc.betslipAdvertActive = false;

    let xpId = '';
    if (typeof ocWindow.xtremepush !== 'undefined') {
      const xpDeviceInfo = ocWindow.xtremepush('get', 'device_info') as { id?: string } | undefined;
      if (typeof xpDeviceInfo !== 'undefined') {
        xpId = xpDeviceInfo.id ?? '';
      }
    }

    const scriptEl = document.createElement('script');
    scriptEl.src = betslipScriptUrl;
    scriptEl.type = 'text/javascript';
    scriptEl.async = true;
    document.head.appendChild(scriptEl);

    const loadBetslip = async () => {
      try {
        await pollForWindowProperty('ocRenderers.integrated-betslip');
      } catch {
        console.warn('OC Betslip renderer not present');
        if (!cancelled) setLoadState('failed');
        return;
      }
      if (cancelled) return;

      ocWindow.ocRenderers['integrated-betslip']({ apiKey, deviceId, oddsFormat, repub, xpId });

      // Wait for getBetCount — a partial { setCallbacks } ocBetslip can appear first.
      try {
        await pollForWindowProperty('ocBetslip.getBetCount');
      } catch {
        console.warn('OC Betslip not loaded');
        if (!cancelled) setLoadState('failed');
        return;
      }
      if (cancelled) return;

      const betslip = ocWindow.ocBetslip;

      // Verify all required methods are present - CDN object is filled progressively
      const hasAllMethods =
        typeof betslip.getBetCount === 'function' &&
        typeof betslip.isInBetslip === 'function' &&
        typeof betslip.addBet === 'function' &&
        typeof betslip.removeBet === 'function' &&
        typeof betslip.toggle === 'function';

      if (!hasAllMethods) {
        console.warn('[UKBetslip] Not all required methods available yet');
        if (!cancelled) setLoadState('failed');
        return;
      }

      // Bridge window.bettingActions.addOrRemoveFromBetslip (called by odds buttons)
      // to window.ocBetslip API (isInBetslip/addBet/removeBet/toggle).
      window.bettingActions = {
        ...(window.bettingActions || {}),
        addOrRemoveFromBetslip: (
          betArray: any[],
          _matchBetIds?: any[],
          bookie?: string,
          betOdds?: string,
          betName?: string,
          betLocation?: string,
          subeventName?: string,
          marketName?: string,
        ) => {
          betArray.forEach((bet: any) => {
            const wasInBetslip = betslip.isInBetslip(bet.betId);

            if (wasInBetslip) {
              betslip.removeBet({ betId: bet.betId });
            } else {
              betslip.addBet({
                betId: bet.betId,
                bookie: bet.selectedBookie || bookie,
                betOdds,
                betName,
                betLocation,
                subeventName,
                marketName,
              } as any);
              // Open betslip drawer after adding bet
              betslip.toggle();
            }
          });
        },
      };

      // CDN calls setBetCount with no args; always read getBetCount().
      betslip.setCallbacks({
        setBetCount: () => setBetCount(betslip.getBetCount()),
      });
      setBetCount(betslip.getBetCount());

      let lastBetCount = betslip.getBetCount();
      intervalId = setInterval(() => {
        const currentCount = betslip.getBetCount();
        if (currentCount === lastBetCount) return;
        setBetCount(currentCount);
        lastBetCount = currentCount;
      }, 1000);

      setLoadState('ready');
    };

    loadBetslip();

    return () => {
      cancelled = true;
      if (intervalId !== null) {
        clearInterval(intervalId);
      }
      scriptEl.remove();
      // Clean up the bridge - don't leave it pointing at a stale betslip closure
      const ocWin = getOcWindow();
      if (ocWin.bettingActions?.addOrRemoveFromBetslip) {
        delete ocWin.bettingActions.addOrRemoveFromBetslip;
      }
    };
  }, [apiKey, betslipScriptUrl, deviceId, oddsFormat, repub]);

  if (loadState === 'failed') return null;

  const isReady = loadState === 'ready';
  const accessibleName = betCount > 0 ? `Betslip, ${betCount} ${betCount === 1 ? 'bet' : 'bets'}` : 'Betslip';

  return (
    <button
      aria-busy={!isReady}
      aria-label={accessibleName}
      className={styles.wrapper}
      data-testid="uk-betslip-button"
      disabled={!isReady}
      onClick={isReady ? () => getOcWindow().ocBetslip.toggle() : undefined}
      type="button"
    >
      <Icon height={28} icon="BetslipUk" width={28} />
      <span aria-hidden="true" className={styles.betCount} data-testid="uk-betslip-count">
        {betCount}
      </span>
    </button>
  );
};
